/**
 * Platform hook for nudge suppression. Wired internally by `ai-proactive-nudge` — do not call directly.
 *
 * Suppression key is read from `nudge.nudgeContext.suppressionKey` (set by the consumer).
 */

import { useCallback, useEffect } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import {
	registerSuppressionDebugAPI,
	unregisterSuppressionDebugAPI,
} from '../../utils/suppression/__debug__/suppression-debug';
import { getSuppressionKey } from '../../utils/suppression/suppression-allowlist';
import {
	isKeySuppressed,
	writeSuppression,
} from '../../utils/suppression/suppression-store';

export type UseNudgeSuppressionResult = {
	/**
	 * Records the nudge as suppressed so it won't show again in this tab session.
	 * Persisted to sessionStorage — survives SPA navigation within the same tab.
	 * No-op if the nudge is not in the suppression allowlist.
	 */
	addSuppressedNudge: (id: string, suppressionKey?: string) => void;
	/**
	 * Returns true if the nudge has an active suppression entry.
	 * Persisted to sessionStorage — survives SPA navigation within the same tab.
	 */
	isSuppressed: (id: string, suppressionKey?: string) => boolean;
};

export const useNudgeSuppression = (): UseNudgeSuppressionResult => {

	const isSuppressed = useCallback(
		(id: string, suppressionKey?: string): boolean => {
			if (!fg('rovo_button_nudge_suppression')) {
				return false;
			}
			const key = getSuppressionKey(id, suppressionKey);
			if (key === null) {
				return false;
			}
			return isKeySuppressed(key);
		},
		[],
	);

	const addSuppressedNudge = useCallback(
		(id: string, suppressionKey?: string): void => {
			if (!fg('rovo_button_nudge_suppression')) {
				return;
			}
			const key = getSuppressionKey(id, suppressionKey);
			if (key === null) {
				return;
			}
			writeSuppression(key);
		},
		[],
	);

	// Expose debug API on window for QA.
	useEffect(() => {
		if (!fg('rovo_button_nudge_suppression')) {
			return;
		}
		registerSuppressionDebugAPI();
		return () => {
			unregisterSuppressionDebugAPI();
		};
	}, []);

	return { isSuppressed, addSuppressedNudge };
};
